import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useEffect, useState } from 'react';
import { Button, Callout, Modal, Popover, Spinner } from '@treams/treams-ui';
import * as graphql from 'generated/graphql';
import { IUserRecipient } from '../../packages/kakapo-ui-services/models/Recipient';
import { RecipientSelectorControl } from '../../packages/kakapo-ui-kit/components/recipients/RecipientSelector';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import RecipientFieldReadonly from 'packages/kakapo-ui-kit/components/recipients/RecipientFieldReadonly';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

export function FeedbackShareModal({
	feedbackRequest,
	onDone,
}: {
	feedbackRequest: graphql.FeedbackRequest;
	onDone: () => void;
}) {
	const { t } = useTranslation();
	const viewer = useViewer();

	// TODO: Maybe also show createdAt field? Could be useful to know when the request was shared.
	const { data, loading } = graphql.useSharedFeedbackRequestQuery({
		variables: { id: feedbackRequest.id },
	});

	const [users, setUsers] = useState<IUserRecipient[]>([]);

	useEffect(() => {
		if (!loading) {
			const initialUsers = data?.feedbackRequest.sharedFeedback.edges.map(
				({ node }) => node.sharedWith,
			);

			setUsers(
				(initialUsers ?? []).map((user) => ({ userId: user.id, userData: user as graphql.User })),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	const exclude = viewer ? [...users, { userId: viewer!.id, userData: viewer! }] : users;

	const [share, { loading: shareLoading }] = graphql.useShareFeedbackRequestMutation();
	const [unshare, { loading: unshareLoading }] = graphql.useRevokeSharedFeedbackMutation();

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header title={t('common.feedback.shareFeedback')} />

				<Modal.Contents>
					<Box mb={4}>
						<RecipientSelectorControl
							// This will force a re-render on select after the length changes
							// this will clear the input field and make the placeholder visible again
							key={exclude.length}
							recipients={exclude}
							onAddRecipient={async (recipient: IUserRecipient) => {
								await share({
									variables: {
										input: { requestID: feedbackRequest.id, sharedWithID: recipient.userId },
									},
								});
								setUsers([...users, recipient as IUserRecipient]);
							}}
							autoFocus
							isDisabled={loading}
						/>
					</Box>
					<Box>
						<Label title={t('common.feedback.sharedWith.label')} />
						<Box mb={[4, 3]}>
							{!loading && users.length === 0 && (
								<Callout color={'yellow'}>{t('common.feedback.sharedWith.empty')} </Callout>
							)}
							{users.map((user) => (
								<RecipientFieldReadonly
									key={user.userId}
									recipient={user}
									onRemove={async () => {
										const userID = user.userId;

										const { data } = await unshare({
											variables: {
												input: {
													requestID: feedbackRequest.id,
													sharedWithID: userID,
												},
											},
										});

										if (data!.revokeSharedFeedback.success) {
											setUsers(users.filter((user) => user.userId !== userID));
										}
									}}
									loading={unshareLoading}
								/>
							))}
							{(shareLoading || loading) && (
								<Flex mt={3} justifyContent="center">
									<Spinner />
								</Flex>
							)}
						</Box>
					</Box>
				</Modal.Contents>
				<Modal.Footer>
					<Button type="primary" title={t('common.done')} onClick={onDone} />
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
